export interface DebouncedFunction<T extends (...args: any[]) => any> {
  (...args: Parameters<T>): void;
  cancel: () => void;
  flush: () => void;
}

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
  immediate = false
): DebouncedFunction<T> {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  let lastArgs: Parameters<T> | null = null;
  let result: ReturnType<T> | undefined;

  const debounced = function (this: any, ...args: Parameters<T>) {
    const context = this;
    lastArgs = args;

    const later = () => {
      timeout = null;
      if (!immediate) {
        result = func.apply(context, lastArgs!);
      }
    };

    const callNow = immediate && !timeout;
    
    if (timeout) {
      clearTimeout(timeout);
    }
    
    timeout = setTimeout(later, wait);

    if (callNow) {
      result = func.apply(context, lastArgs!);
    }

    return result;
  } as DebouncedFunction<T>;

  debounced.cancel = () => {
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
    }
  };

  debounced.flush = () => {
    if (timeout) {
      clearTimeout(timeout);
      if (lastArgs) {
        const context = debounced as any;
        result = func.apply(context, lastArgs);
      }
      timeout = null;
    }
  };

  return debounced;
}